<template>
  <div>
    <b-row>
      <b-col md="3">

        <b-row>
          <b-col>
            <b-card
              v-if="patients.length > 1"
              class="mb-50"
              no-body
            >
              <b-card-body class="pt-1 px-1 pb-0">
                <b-form-group :label="$t('order.patient-info.select')">
                  <b-form-select
                    v-model.number="activePatientIndex"
                    :options="patientOptions"
                    :disabled="!patients.length"
                  />
                </b-form-group>
              </b-card-body>
            </b-card>

            <b-button
              v-if="canAddPatient"
              variant="flat-primary"
              class="btn-block text-left mt-1"
              @click="openAddPatientModal"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-25"
              />
              {{ $t('order.patient-info.list.add') }}
            </b-button>

            <add-patient-modal
              ref="addPatientModal"
              :can-add-patient="canAddPatient"
              @patient-added="handlePatientAdded"
            />

            <router-link
              v-for="(section, sectionName) in sections"
              v-show="section.show"
              :key="sectionName"
              class="btn btn-light btn-block text-left"
              :class="{'btn-primary': section.isCurrent, 'btn-dark': isDark}"
              :to="{ name: $route.name, params: {tab: 'patient-info', section: sectionName}}"
            >
              {{ section.title }}
            </router-link>

            <router-link
              v-if="!isAttachmentsOnlyDocumentation && patients.length"
              class="btn btn-light btn-block text-left mt-2"
              :class="{'disabled': unsavedChanges, 'btn-dark': isDark}"
              :to="{ name: 'order-order', params: {id: order.id}}"
            >
              <feather-icon icon="PrinterIcon" />
              {{ $t('shared.button.print') }}
            </router-link>

            <div
              v-if="!isAttachmentsOnlyDocumentation && patients.length"
              class="btn btn-light btn-block text-left mb-2"
              :class="{'disabled': !unsavedChanges, 'btn-dark': isDark}"
              @click="saveChanges"
            >
              <feather-icon icon="SaveIcon" />
              {{ $t('shared.button.submit') }}
            </div>
          </b-col>
        </b-row>

      </b-col>

      <b-col md="9">
        <validation-observer ref="patientInfoValidation">
          <component
            :is="currentSectionConfig.component"
            v-if="currentSectionConfig && currentSectionConfig.show"
            :key="`${currentSection}-${activePatientIndex}`"
            class="md:w-4/5"
            :order="order"
            :patient="activePatient"
            :commit-order="commitOrder"
          />
        </validation-observer>
      </b-col>

    </b-row>
  </div>
</template>

<script>
import PatientData from '@/views/orders/partials/patient-info/PatientData.vue'
import HealthCondition from '@/views/orders/partials/patient-info/HealthCondition.vue'
import Diagnosis from '@/views/orders/partials/patient-info/Diagnosis.vue'
import ProvidedHealthServices from '@/views/orders/partials/patient-info/ProvidedHealthServices.vue'
import Death from '@/views/orders/partials/patient-info/Death.vue'
import TransportSummary from '@/views/orders/partials/patient-info/TransportSummary.vue'
import Attachments from '@/views/orders/partials/patient-info/Attachments.vue'
import AddPatientModal from '@/views/orders/partials/AddPatientModal.vue'
import { cloneNested, parseRequestError } from '@/helpers/helpers'
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import orderTypes from '@/dictionaries/orderTypes'
import orderEventKinds from '@/dictionaries/orderEventKinds'
import { isPatientVisibleInView } from '@/helpers/patient'

export default {
  name: 'OrderTabPatientInfo',
  components: {
    PatientData,
    HealthCondition,
    Diagnosis,
    ProvidedHealthServices,
    Death,
    TransportSummary,
    Attachments,
    AddPatientModal,
    ValidationObserver,
  },
  data() {
    return {
      order: cloneNested(this.$store.getters['order/getOrderData']),
      saveChangesTimout: null,
      unsavedChanges: false,
      activePatientIndex: 0,
    }
  },
  computed: {
    orderEdited() {
      return this.$store.getters['order/getOrderData']
    },
    workflow() {
      return this.$store.getters['order/getOrderWorkflow']
    },
    patients() {
      const patients = Array.isArray(this.order.patientInfo) ? this.order.patientInfo : []
      return patients.filter(patient => isPatientVisibleInView(patient))
    },
    patientOptions() {
      if (!this.patients.length) {
        return [{ value: 0, text: this.$t('order.patient-info.list.empty') }]
      }
      return this.patients.map((patient, index) => {
        const labelParts = [patient?.surname, patient?.name].filter(Boolean)
        const label = labelParts.length
          ? labelParts.join(' ')
          : this.$t('order.patient-info.single-title', {
            index: index + 1,
          })
        return {
          value: index,
          text: label,
        }
      })
    },
    isAttachmentsOnlyDocumentation() {
      if (!this.orderEdited) {
        return false
      }
      if (this.orderEdited.type === orderTypes.medical_courier) {
        return true
      }
      return this.orderEdited.type === orderTypes.event
          && this.orderEdited.eventKind === orderEventKinds.medical_training
    },
    canAddPatient() {
      return !!this.workflow?.patientActions?.add?.allowed
    },
    activePatient() {
      if (!this.patients.length) {
        return null
      }
      const index = Math.min(this.activePatientIndex, this.patients.length - 1)
      return this.patients[index]
    },
    currentSection() {
      let current = 'patient-data'
      if (this.isAttachmentsOnlyDocumentation) {
        current = 'attachments'
      }
      return this.$route.params.section ? this.$route.params.section : current
    },
    currentSectionConfig() {
      return this.sections[this.currentSection] || null
    },
    sections() {
      return {
        'patient-data': {
          title: this.$t('order.view.tabs.patient-info.patient-data.title'),
          component: 'PatientData',
          isCurrent: this.currentSection === 'patient-data',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        'health-condition': {
          title: this.$t('order.view.tabs.patient-info.health-condition.title'),
          component: 'HealthCondition',
          isCurrent: this.currentSection === 'health-condition',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        diagnosis: {
          title: this.$t('order.view.tabs.patient-info.diagnosis.title'),
          component: 'Diagnosis',
          isCurrent: this.currentSection === 'diagnosis',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        'provided-health-services': {
          title: this.$t('order.view.tabs.patient-info.provided-health-services.title'),
          component: 'ProvidedHealthServices',
          isCurrent: this.currentSection === 'provided-health-services',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        death: {
          title: this.$t('order.view.tabs.patient-info.death.title'),
          component: 'Death',
          isCurrent: this.currentSection === 'death',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        summary: {
          title: this.$t('order.view.tabs.patient-info.summary.title'),
          component: 'TransportSummary',
          isCurrent: this.currentSection === 'summary',
          show: !this.isAttachmentsOnlyDocumentation,
          roles: {
            view: ['ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER'],
          },
        },
        attachments: {
          title: this.$t('order.view.tabs.patient-info.attachments.title'),
          component: 'Attachments',
          isCurrent: this.currentSection === 'attachments',
          show: true,
          roles: {
            view: ['ROLE_TEAM_LEADER', 'ROLE_DISPATCHER'],
            edit: ['ROLE_TEAM_LEADER', 'ROLE_DISPATCHER'],
          },
        },
      }
    },
  },
  watch: {
    orderEdited: {
      deep: true,
      handler(newOrder) {
        if (this.unsavedChanges) {
          return
        }
        this.order = cloneNested(newOrder)
        this.ensureActivePatientIndex()
      },
    },
    patients() {
      this.ensureActivePatientIndex()
    },
  },
  beforeDestroy() {
    clearTimeout(this.saveChangesTimout)
    this.saveChanges()
  },
  methods: {
    buildPatientUpdatePayload(patient) {
      const normalizedPatient = cloneNested(patient)
      normalizedPatient.address = this.normalizeAddressReference(normalizedPatient.address)
      normalizedPatient.addressDetails = this.normalizeAddressReference(normalizedPatient.addressDetails)

      return {
        version: normalizedPatient.version,
        name: normalizedPatient.name,
        surname: normalizedPatient.surname,
        age: normalizedPatient.age,
        sex: normalizedPatient.sex,
        pesel: normalizedPatient.pesel,
        notes: normalizedPatient.notes,
        weight: normalizedPatient.weight,
        patientOnBoard: normalizedPatient.patientOnBoard,
        patientTransportStatus: normalizedPatient.patientTransportStatus,
        address: normalizedPatient.address,
        addressDetails: normalizedPatient.addressDetails,
        patientData: normalizedPatient.patientData,
        death: normalizedPatient.death,
        summary: normalizedPatient.summary,
        providedHealthServices: normalizedPatient.providedHealthServices,
        diagnosis: normalizedPatient.diagnosis,
        healthCondition: normalizedPatient.healthCondition,
      }
    },
    normalizeAddressReference(value) {
      if (value && typeof value === 'object') {
        return value.id || null
      }
      return value || null
    },
    commitOrder() {
      this.$store.commit('order/SET_ORDER_EDITED', this.order)
      this.unsavedChanges = true
      this.setSaveChangesTimeout()
    },
    setSaveChangesTimeout() {
      clearTimeout(this.saveChangesTimout)
      this.saveChangesTimout = setTimeout(() => {
        this.saveChanges()
      }, 5000)
    },
    async saveChanges() {
      if (!this.unsavedChanges) {
        return Promise.resolve()
      }

      const patient = this.activePatient
      if (!patient || !patient.id) {
        this.unsavedChanges = false
        window.toast.notify.warning(this.$t('order.patient-info.select'))
        return Promise.resolve()
      }

      const validationObserver = this.$refs.patientInfoValidation
      if (validationObserver && typeof validationObserver.validate === 'function') {
        const isValid = await validationObserver.validate()
        if (!isValid) {
          return Promise.resolve()
        }
      }

      this.unsavedChanges = false
      const payload = {
        id: patient.id,
        orderId: this.order.id,
        data: this.buildPatientUpdatePayload(patient),
      }
      return this.$store.dispatch('order/updateOrderPatientData', payload)
        .then(() => {
          window.toast.notify.success(this.$t('order.notify.edit.success_title'))
          this.$emit('save-complete')
        })
        .catch(err => {
          parseRequestError(err)
        })
    },
    openAddPatientModal() {
      const modal = this.$refs.addPatientModal
      if (modal && typeof modal.open === 'function') {
        Promise.resolve(this.saveChanges())
          .then(() => {
            modal.open()
          })
      }
    },
    handlePatientAdded(patientInfoId) {
      if (!patientInfoId) {
        this.ensureActivePatientIndex()
        return
      }

      const nextIndex = this.patients.findIndex(patient => String(patient?.id) === String(patientInfoId))
      if (nextIndex >= 0) {
        this.activePatientIndex = nextIndex
        return
      }

      this.ensureActivePatientIndex()
    },
    ensureActivePatientIndex() {
      if (!this.patients.length) {
        this.activePatientIndex = 0
        return
      }
      if (this.activePatientIndex > this.patients.length - 1) {
        this.activePatientIndex = this.patients.length - 1
      }
      if (this.activePatientIndex < 0) {
        this.activePatientIndex = 0
      }
    },
  },
  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')
    return { skin, isDark }
  },
}
</script>
